import { t } from 'i18next'
import { getAge, getBMI, getBadHabits, getBreakfastTime, getCurrentBodyType, getDesiredBodyType, getDinnerTime, getEmail, getEnergyLevelInfo, getFastingFeelings, getFastingKnowledge, getFastingMotivation, getGoal, getHappyWeightInfo, getHeight, getHomeOrOutside, getLastHappyWeight, getLunchTime, getOnePlan, getOutOfBreath, getPerfectWeight, getPlanIsReady, getProgressStep, getSleepQuality, getSpecialOccasion, getSpecialOccasionDate, getTargetZones, getTrackedHours, getWalkingTime, getWaterIntake, getWeight, getWorkActivityLevel, getWorkloadFrequency, getYourGoalsInfo } from './steps'
import { getGenderSelection } from './steps/GenderSelection'
import { getReceiveEmail } from './steps/ReceiveEmail'
import { getReinforcementBeforeAfter } from './steps/ReinforcementBeforeAfter'
import { getReinforcementScientificMethod } from './steps/ReinforcementScientificMethod'
import { existingEmailLogin } from './steps/ExistingEmailLogin'
import { getAgeRange } from './steps/AgeRange'
import { getBMIInfo } from './steps/BMIInfo'
import { getOutOfBreathInfo } from './steps/OutOfBreathInfo'
import { getExperienceInfo } from './steps/ExperienceInfo'
import { OnboardingStep } from 'types/onboardingTypes'

interface IGetStepConfigsArgs {
  gender?: IGender
  onboardingStepsVariant?: string
  activeInfoScreens: { [key: string]: boolean}
}

function getStepConfigs({ gender, onboardingStepsVariant, activeInfoScreens }: IGetStepConfigsArgs): OnboardingStep[] {
  // First step is always considered to be the gender selection screen. Should this change, appropriate changes should be made in the getFirstStep function as well.
  const genderSelection = getGenderSelection(t)
  const goal = getGoal(t, gender)
  const ageRange = getAgeRange(t)
  const trackedHours = getTrackedHours(t)
  const desiredBodyType = getDesiredBodyType(t, gender)
  const currentBodyType = getCurrentBodyType(t, gender)
  const targetZones = getTargetZones(t, gender)
  const lastHappyWeight = getLastHappyWeight(t)
  const breakfastTime = getBreakfastTime(t)
  const lunchTime = getLunchTime(t)
  const dinnerTime = getDinnerTime(t)
  const homeOrOutside = getHomeOrOutside(t)
  const reinforcementScientificMethod = getReinforcementScientificMethod(t)
  const fastingFeelings = getFastingFeelings(t)
  const workloadFrequency = getWorkloadFrequency(t)
  const workActivityLevel = getWorkActivityLevel(t)
  const outOfBreath = getOutOfBreath(t)
  const walkingTime = getWalkingTime(t)
  const waterIntake = getWaterIntake(t)
  const sleepQuality = getSleepQuality(t)
  const badHabits = getBadHabits(t)
  const reinforcementBeforeAfter = getReinforcementBeforeAfter(t, gender)
  const fastingKnowledge = getFastingKnowledge(t)
  const fastingMotivation = getFastingMotivation(t)
  const height = getHeight(t)
  const weight = getWeight(t)
  const perfectWeight = getPerfectWeight(t)
  const age = getAge(t)
  const bmi = getBMI(t)
  const specialOccasion = getSpecialOccasion(t)
  const specialOccasionDate = getSpecialOccasionDate(t)
  const theOnePlan = getOnePlan(t)
  const progressStep = getProgressStep(t)
  const email = getEmail(t)
  const existingEmail = existingEmailLogin(t)
  const receiveEmail = getReceiveEmail(t)
  const planIsReady = getPlanIsReady(t)
  const yourGoalsInfo = activeInfoScreens?.yourGoalsInfo ? getYourGoalsInfo() : null
  const happyWeightInfo = activeInfoScreens?.happyWeightInfo ? getHappyWeightInfo() : null
  const energyLevelInfo = activeInfoScreens?.energyLevelInfo ? getEnergyLevelInfo() : null
  const outOfBreathInfo = activeInfoScreens?.outOfBreathInfo ? getOutOfBreathInfo() : null
  const experienceInfo = activeInfoScreens?.experienceInfo ? getExperienceInfo() : null
  const bmiInfo = activeInfoScreens?.bmiInfo ? getBMIInfo() : null

  const stepListV1 = [
    genderSelection,
    trackedHours,
    goal,
    yourGoalsInfo,
    desiredBodyType,
    currentBodyType,
    targetZones,
    lastHappyWeight,
    happyWeightInfo,
    breakfastTime,
    lunchTime,
    dinnerTime,
    homeOrOutside,
    reinforcementScientificMethod,
    fastingFeelings,
    energyLevelInfo,
    workloadFrequency,
    workActivityLevel,
    outOfBreath,
    outOfBreathInfo,
    walkingTime,
    waterIntake,
    sleepQuality,
    badHabits,
    reinforcementBeforeAfter,
    fastingKnowledge,
    experienceInfo,
    fastingMotivation,
    height,
    weight,
    perfectWeight,
    age,
    bmi,
    bmiInfo,
    specialOccasion,
    specialOccasionDate,
    theOnePlan,
    progressStep,
    email,
    existingEmail,
    receiveEmail,
    planIsReady
  ]

  const stepListV2 = [
    genderSelection,
    trackedHours,
    goal,
    yourGoalsInfo,
    ageRange,
    desiredBodyType,
    currentBodyType,
    targetZones,
    lastHappyWeight,
    happyWeightInfo,
    breakfastTime,
    lunchTime,
    dinnerTime,
    homeOrOutside,
    reinforcementScientificMethod,
    fastingFeelings,
    energyLevelInfo,
    workloadFrequency,
    workActivityLevel,
    outOfBreath,
    outOfBreathInfo,
    walkingTime,
    waterIntake,
    sleepQuality,
    badHabits,
    reinforcementBeforeAfter,
    fastingKnowledge,
    experienceInfo,
    fastingMotivation,
    height,
    weight,
    perfectWeight,
    bmi,
    bmiInfo,
    specialOccasion,
    specialOccasionDate,
    theOnePlan,
    progressStep,
    email,
    existingEmail,
    receiveEmail,
    planIsReady
  ]

  // Filter out any null or undefined steps from the step list
  return ((onboardingStepsVariant === 'v2' ? stepListV2 : stepListV1).filter(Boolean)) as OnboardingStep[]
}

export function getFirstStep() {
  return getGenderSelection(t)
}

export default getStepConfigs
